/**
 * Reading the firm out of the address bar.
 *
 * A firm that bought the add-on gets `cabinet.formio.ca`, and its clients open
 * their form there. The client id in the path still decides whose dossier is
 * being filled — the host only says which firm's colors the page wears, which
 * is why it can be resolved before any client data has loaded.
 */

const ROOT_LABELS = 2; // formio.ca

/** Hosts that are ours, not a firm's. Mirrors RESERVED_SUBDOMAINS server-side. */
const NOT_A_FIRM = new Set(["app", "www", "api", "admin", "console", "staging", "dev", "test"]);

/**
 * `cabinet.formio.ca` → `"cabinet"`. Anything that can't be a firm address
 * (localhost, a Vercel preview, `app.formio.ca`, the bare root) → `null`.
 */
export function subdomainFromHost(hostname: string): string | null {
  const host = hostname.trim().toLowerCase();
  if (!host || host === "localhost" || /^[\d.]+$/.test(host)) return null;

  // Browsers resolve any `*.localhost` to the loopback, so `cabinet.localhost:8081`
  // exercises this path in dev without touching DNS.
  if (host.endsWith(".localhost")) {
    const label = host.slice(0, -".localhost".length);
    return label && !label.includes(".") && !NOT_A_FIRM.has(label) ? label : null;
  }

  if (!host.endsWith(".formio.ca")) return null;

  const labels = host.split(".");
  // Exactly one label in front of the root: `a.b.formio.ca` is not an address
  // we hand out, so it resolves to nothing rather than to `a`.
  if (labels.length !== ROOT_LABELS + 1) return null;

  const label = labels[0];
  if (!label || NOT_A_FIRM.has(label)) return null;
  return label;
}

/** The subdomain this page was opened on, if any. */
export function currentSubdomain(): string | null {
  if (typeof window === "undefined") return null;
  return subdomainFromHost(window.location.hostname);
}

/** Hosts we serve, and can therefore rewrite. A preview build has no wildcard. */
function isOurHost(hostname: string): boolean {
  return (
    hostname === "formio.ca" ||
    hostname.endsWith(".formio.ca") ||
    hostname === "localhost" ||
    hostname.endsWith(".localhost")
  );
}

/**
 * Where this page *should* be, given the firm's own address.
 *
 * Every hostname resolves — that's the wildcard, and it's what keeps a link that
 * was sent before a firm claimed its address (or with a typo in front) working.
 * The cost is that the same form answers under any spelling. So once we know the
 * firm's address, the client is moved onto it: the path is untouched, only the
 * name in front changes, and the client ends up seeing the firm they're dealing
 * with rather than whatever the link happened to say.
 *
 * Returns null when there is nothing to do: no address, not one of our hosts, or
 * already in the right place.
 */
export function canonicalUrlFor(currentHref: string, subdomain: string | null | undefined): string | null {
  const sub = (subdomain ?? "").trim().toLowerCase();
  if (!sub) return null;

  let url: URL;
  try {
    url = new URL(currentHref);
  } catch {
    return null;
  }
  if (!isOurHost(url.hostname)) return null;

  const labels = url.hostname.split(".");
  // Combien de labels valent la RACINE, sans cabinet devant : `formio.ca` en
  // compte deux, `localhost` un seul.
  const estLocal = url.hostname === "localhost" || url.hostname.endsWith(".localhost");
  const labelsRacine = estLocal ? 1 : ROOT_LABELS;
  // On garde la racine et on met le cabinet devant, au lieu de raisonner sur
  // « remplacer le premier label ou non ». Cette distinction-là comptait deux
  // labels de racine partout : `cabinet.localhost` passait donc pour une racine
  // nue et recevait le cabinet une SECONDE fois, d'où le
  // `blouin.blouin.localhost` observé en développement, un hôte que le proxy
  // Gemini refuse en CORS — plus aucune lecture de document en local.
  // Reformulé ainsi, la profondeur ne change plus rien : une adresse déjà
  // doublée se remet d'aplomb au lieu d'y rester coincée.
  const target = [sub, ...labels.slice(-labelsRacine)].join(".");
  if (target === url.hostname) return null;

  url.hostname = target;
  return url.toString();
}
